import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, NgZone, OnDestroy, afterRenderEffect, computed, inject, input, output, signal, untracked, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SocialWallAction } from '../social-wall-detail-dialog/social-wall-detail-dialog.component';
import { SocialWallDialogService } from '../social-wall-detail-dialog/social-wall-dialog.service';

export interface SocialWallCard {
  key: string;
  index: number;
  item: any;
}

const ESTIMATED_CARD_HEIGHT = 320;
const COLUMN_GAP = 24;
const MAX_CORRECTION_PASSES = 3;

@Component({
  selector: 'lib-social-wall',
  templateUrl: './social-wall.component.html',
  styleUrl: './social-wall.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SocialWallComponent implements AfterViewInit, OnDestroy {

  public items = input<any[]>();
  public maxColumns = input(3);
  public minColumnWidth = input(300);

  public postAction = output<SocialWallAction>();

  public columnCount = signal(0);
  public isSettled = signal(false);
  public cards = computed<SocialWallCard[]>(() =>
    (this.items() ?? []).map((item, index) => ({ key: String(item?.id ?? index), index, item })));
  public columns = computed(() => this.distributeIntoColumns(this.cards(), this.columnCount(), this.measuredHeights()));
  private cardElements = viewChildren<ElementRef<HTMLElement>>('cardElement');
  private measuredHeights = signal<ReadonlyMap<string, number>>(new Map());

  private readonly element = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly socialWallDialogService = inject(SocialWallDialogService);

  private resizeObserver: ResizeObserver | null = null;
  private scheduledFrame: number | null = null;
  private pendingContainerWidth: number | null = null;
  private correctionPasses = 0;
  private hasSettled = false;

  constructor() {
    afterRenderEffect({ read: () => {
      this.cardElements();
      this.measureCards();
    }});
  }

  ngAfterViewInit(): void {
    const host = this.element.nativeElement as HTMLElement;

    this.ngZone.runOutsideAngular(() => {
      this.scheduleUpdate(host.clientWidth);

      this.resizeObserver = new ResizeObserver(entries => this.scheduleUpdate(entries[0]?.contentRect.width));
      this.resizeObserver.observe(host);

      host.addEventListener('load', this.onCardImageLoad, true);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.element.nativeElement.removeEventListener('load', this.onCardImageLoad, true);

    if (this.scheduledFrame !== null) {
      cancelAnimationFrame(this.scheduledFrame);
    }
  }

  public openDetail(index: number): void {
    const dialog = this.socialWallDialogService.openDetail({ items: this.items() ?? [], index });

    dialog.componentInstance.action
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(action => this.postAction.emit(action));
  }

  private onCardImageLoad = (): void => this.scheduleUpdate();

  private scheduleUpdate(containerWidth?: number): void {
    this.pendingContainerWidth = containerWidth || this.pendingContainerWidth;

    if (this.scheduledFrame !== null) {
      return;
    }

    this.scheduledFrame = requestAnimationFrame(() => {
      this.scheduledFrame = null;

      if (this.pendingContainerWidth) {
        this.updateColumnCount(this.pendingContainerWidth);
        this.pendingContainerWidth = null;
      }

      this.measureCards();
    });
  }

  private updateColumnCount(containerWidth: number): void {
    const columnsThatFit = Math.floor((containerWidth + COLUMN_GAP) / (this.minColumnWidth() + COLUMN_GAP));
    const columnCount = Math.max(1, Math.min(this.maxColumns(), columnsThatFit));

    if (columnCount !== untracked(this.columnCount)) {
      this.columnCount.set(columnCount);
    }
  }

  private findShortestColumn(columnHeights: number[]): number {
    let shortest = 0;

    for (let column = 1; column < columnHeights.length; column++) {
      if (columnHeights[column] < columnHeights[shortest]) {
        shortest = column;
      }
    }

    return shortest;
  }

  private distributeIntoColumns(
    cards: SocialWallCard[],
    columnCount: number,
    measuredHeights: ReadonlyMap<string, number>,
    estimatedHeight = ESTIMATED_CARD_HEIGHT,
    gapBetweenCards = COLUMN_GAP): SocialWallCard[][] {
    const totalColumns = Math.max(1, columnCount);

    const columns: SocialWallCard[][] = Array.from({ length: totalColumns }, () => []);
    const columnHeights: number[] = new Array(totalColumns).fill(0);

    for (const card of cards) {
      const shortest = this.findShortestColumn(columnHeights);
      const cardHeight = measuredHeights.get(card.key) ?? estimatedHeight;
      const isFirstInColumn = !columns[shortest].length;

      columns[shortest].push(card);
      columnHeights[shortest] += cardHeight + (isFirstInColumn ? 0 : gapBetweenCards);
    }

    return columns;
  }

  private measureCards(): void {
    const cardElements = untracked(this.cardElements);

    if (!cardElements.length) {
      return;
    }

    const heights = new Map(untracked(this.measuredHeights));
    let hasChanged = false;

    for (const cardElement of cardElements) {
      const element = cardElement.nativeElement;
      const key = element.dataset['key'];
      const height = element.offsetHeight;

      if (!key || !height || heights.get(key) === height) {
        continue;
      }

      heights.set(key, height);
      hasChanged = true;
    }

    if (hasChanged) {
      this.measuredHeights.set(heights);
    }

    this.revealWhenSettled(hasChanged);
  }

  private revealWhenSettled(hasChanged: boolean): void {
    if (this.hasSettled) {
      return;
    }

    if (hasChanged && ++this.correctionPasses < MAX_CORRECTION_PASSES) {
      return;
    }

    this.hasSettled = true;
    this.isSettled.set(true);
  }
}
